import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ReceiptClaimView,
  ReservationListView,
  ReservationSettingsView,
  ReservationSlotsView,
  ReservationView,
} from '@ulp/consumer-core';
import { apiFetch } from './api';
import { useSession } from './session';
import { useTenant } from './tenant';

function useK() {
  const { slug } = useTenant();
  const { memberId, token } = useSession();
  return { enabled: !!token, k: (...parts: unknown[]) => [slug, memberId, 'reservations', ...parts] };
}

export function useReservationSettings() {
  const { k, enabled } = useK();
  return useQuery({
    queryKey: k('settings'),
    queryFn: () => apiFetch<ReservationSettingsView>('/app/reservations/settings'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useReservationSlots(q: { branchId: string | null; date: string; partySize: number }) {
  const { k, enabled } = useK();
  const params = new URLSearchParams({ date: q.date, partySize: String(q.partySize) });
  if (q.branchId) params.set('branchId', q.branchId);
  return useQuery({
    queryKey: k('slots', q.branchId, q.date, q.partySize),
    queryFn: () => apiFetch<ReservationSlotsView>(`/app/reservations/slots?${params.toString()}`),
    enabled: enabled && !!q.branchId,
    staleTime: 30_000,
  });
}

export function useReservations() {
  const { k, enabled } = useK();
  return useQuery({
    queryKey: k('list'),
    queryFn: () => apiFetch<ReservationListView>('/app/reservations'),
    enabled,
    // Onay bekleyen varsa durumu sık tazele (işletme onayı).
    refetchInterval: (q) => (q.state.data?.upcoming.some((r) => r.status === 'PENDING') ? 15_000 : false),
  });
}

export function useReservation(id: string | undefined) {
  const { k, enabled } = useK();
  return useQuery({
    queryKey: k('one', id),
    queryFn: () => apiFetch<ReservationView>(`/app/reservations/${id}`),
    enabled: enabled && !!id,
    refetchInterval: (q) => (q.state.data?.status === 'PENDING' ? 10_000 : false),
  });
}

export interface CreateReservationInput {
  clientRequestId: string;
  branchId: string;
  startsAt: string;
  partySize: number;
  contactName: string;
  contactPhone?: string;
  note?: string;
}

export function useCreateReservation() {
  const qc = useQueryClient();
  const { k } = useK();
  return useMutation({
    mutationFn: (input: CreateReservationInput) =>
      apiFetch<ReservationView>('/app/reservations', {
        method: 'POST',
        body: input,
        headers: { 'idempotency-key': input.clientRequestId },
        timeoutMs: 20_000,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: k() }),
  });
}

export function useCancelReservation() {
  const qc = useQueryClient();
  const { k } = useK();
  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      apiFetch<ReservationView>(`/app/reservations/${input.id}/cancel`, { method: 'POST', body: { reason: input.reason } }),
    onSuccess: (data) => {
      qc.setQueryData(k('one', data.id), data);
      void qc.invalidateQueries({ queryKey: k('list') });
    },
  });
}

// ---- Fişimi ekle (D9) ----

export function useReceiptClaims() {
  const { slug } = useTenant();
  const { memberId, signedIn } = useSession();
  return useQuery({
    queryKey: [slug, memberId, 'receipts'],
    queryFn: () => apiFetch<ReceiptClaimView[]>('/app/receipts/claims'),
    enabled: signedIn,
  });
}

export function useClaimReceipt() {
  const qc = useQueryClient();
  const { slug } = useTenant();
  const { memberId } = useSession();
  return useMutation({
    mutationFn: (input: { branchId?: string; receiptNo: string; totalKurus: number; purchasedOn: string }) =>
      apiFetch<ReceiptClaimView>('/app/receipts/claim', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [slug, memberId, 'receipts'] });
      void qc.invalidateQueries({ queryKey: [slug, memberId, 'home'] });
    },
  });
}

export const RESERVATION_STATUS: Record<string, { label: string; tone: 'gold' | 'success' | 'danger' | 'neutral' | 'brand' | 'warning' }> = {
  PENDING: { label: 'Onay bekliyor', tone: 'warning' },
  CONFIRMED: { label: 'Onaylandı', tone: 'success' },
  REJECTED: { label: 'Reddedildi', tone: 'danger' },
  CANCELLED: { label: 'İptal edildi', tone: 'neutral' },
  SEATED: { label: 'Masaya oturuldu', tone: 'brand' },
  NO_SHOW: { label: 'Gelinmedi', tone: 'neutral' },
  COMPLETED: { label: 'Tamamlandı', tone: 'neutral' },
};
